import moment, { Moment } from 'moment'
import { isFactTimeDeviation } from 'components/plan/feedPlanDeviation'
import { IFeed, IFeedFact } from 'models/IFeed'
import { MetricBadge, MetricCardData, MetricPart } from './dayMetrics'

export type CleaningScope = {
	groupIds: number[]
	multipleGroups: boolean
	period: boolean
}

type DaySample = {
	groupId: number
	groupTitle: string
	date: string
	remainingKg: number
	distributedKg: number
}

type CleaningFact = {
	groupId: number
	groupTitle: string
	date: string
	planTime?: string
	factDt?: number | null
	weightKg: number
	deviation?: number | string
	recorded: boolean
}

const value = (text: string): MetricPart => ({ text, tone: 'value' })
const unit = (text: string): MetricPart => ({ text, tone: 'unit' })

const formatInt = (amount: number) => Math.round(amount).toLocaleString('ru-RU')

const toMoment = (stamp?: number | null) => {
	if (stamp == null) return null
	return stamp < 1e12 ? moment.unix(stamp) : moment(stamp)
}

const weightKg = (fact: IFeedFact) => {
	const amount = fact.weight ?? fact.food_weight
	return typeof amount === 'number' && !Number.isNaN(amount) ? amount : 0
}

const inScope = (groupId: number | undefined, scope: CleaningScope) =>
	groupId != null &&
	(scope.groupIds.length === 0 || scope.groupIds.includes(groupId))

const collectDay = (day: string, plans: IFeed[], scope: CleaningScope) => {
	const samples = new Map<number, DaySample>()
	const cleanings: CleaningFact[] = []

	const sampleOf = (groupId: number, groupTitle: string) => {
		const current = samples.get(groupId)
		if (current) return current
		const created: DaySample = {
			groupId,
			groupTitle,
			date: day,
			remainingKg: 0,
			distributedKg: 0,
		}
		samples.set(groupId, created)
		return created
	}

	plans.forEach(plan => {
		plan.feed_facts?.forEach(fact => {
			const groupId = fact.group?.id
			if (!inScope(groupId, scope) || groupId == null) return
			const title = fact.group?.title || ''

			if (plan.type === 'distribution' && fact.fact_dt) {
				sampleOf(groupId, title).distributedKg += weightKg(fact)
			}

			if (plan.type !== 'cleaning') return

			const recorded = Boolean(fact.fact_dt)
			if (recorded) {
				sampleOf(groupId, title).remainingKg += weightKg(fact)
			}
			cleanings.push({
				groupId,
				groupTitle: title,
				date: day,
				planTime: fact.plan_time || plan.time,
				factDt: fact.fact_dt,
				weightKg: weightKg(fact),
				deviation: plan.deviation,
				recorded,
			})
		})
	})

	return { samples: Array.from(samples.values()), cleanings }
}

const percentOf = (sample: DaySample) =>
	sample.distributedKg > 0
		? (sample.remainingKg / sample.distributedKg) * 100
		: null

const groupBadge = (title: string): MetricBadge => ({
	text: title,
	tone: 'neutral',
})

const alertBadge = (text: string): MetricBadge => ({
	text,
	tone: 'alert',
})

export const buildCleaningCards = (
	days: { day: string; plans: IFeed[] }[],
	scope: CleaningScope,
): MetricCardData[] => {
	const samples: DaySample[] = []
	const cleanings: CleaningFact[] = []

	days.forEach(item => {
		const collected = collectDay(item.day, item.plans, scope)
		samples.push(...collected.samples)
		cleanings.push(...collected.cleanings)
	})

	const totalRemaining = samples.reduce(
		(sum, sample) => sum + sample.remainingKg,
		0,
	)

	const byGroup = new Map<number, DaySample>()
	samples.forEach(sample => {
		const current = byGroup.get(sample.groupId) || {
			...sample,
			remainingKg: 0,
			distributedKg: 0,
		}
		current.remainingKg += sample.remainingKg
		current.distributedKg += sample.distributedKg
		current.groupTitle = sample.groupTitle
		byGroup.set(sample.groupId, current)
	})

	const groupPercents = Array.from(byGroup.values())
		.map(sample => ({ sample, percent: percentOf(sample) }))
		.filter(
			(item): item is { sample: DaySample; percent: number } =>
				item.percent != null,
		)

	const dayPercents = samples
		.map(sample => ({ sample, percent: percentOf(sample) }))
		.filter(
			(item): item is { sample: DaySample; percent: number } =>
				item.percent != null,
		)

	const extremeSource = scope.period ? dayPercents : groupPercents
	const minRemaining = extremeSource.reduce<
		{ sample: DaySample; percent: number } | undefined
	>(
		(best, item) => (!best || item.percent < best.percent ? item : best),
		undefined,
	)
	const maxRemaining = extremeSource.reduce<
		{ sample: DaySample; percent: number } | undefined
	>(
		(best, item) => (!best || item.percent > best.percent ? item : best),
		undefined,
	)

	const avgRemaining =
		groupPercents.length > 0
			? groupPercents.reduce((sum, item) => sum + item.percent, 0) /
				groupPercents.length
			: null

	const recorded = cleanings.filter(item => item.recorded)
	const onTime = recorded.filter(
		item =>
			!isFactTimeDeviation(
				item.factDt,
				item.planTime,
				item.date,
				item.deviation,
			),
	)
	const compliance =
		recorded.length > 0 ? (onTime.length / recorded.length) * 100 : null
	const late = compliance == null ? null : 100 - compliance

	let maxDeviation: {
		minutes: number
		fact: Moment
		groupTitle: string
	} | null = null
	recorded.forEach(item => {
		const fact = toMoment(item.factDt)
		if (!fact || !item.planTime) return
		const [hours, minutes] = item.planTime.slice(0, 5).split(':').map(Number)
		if (Number.isNaN(hours) || Number.isNaN(minutes)) return
		const plan = moment(item.date).set({
			hour: hours,
			minute: minutes,
			second: 0,
			millisecond: 0,
		})
		const diff = fact.diff(plan, 'minutes', true)
		if (!maxDeviation || diff > maxDeviation.minutes) {
			maxDeviation = { minutes: diff, fact, groupTitle: item.groupTitle }
		}
	})

	const plannedCount = cleanings.length
	const missedCount = plannedCount - recorded.length
	const missedPercent =
		plannedCount > 0 ? (missedCount / plannedCount) * 100 : null

	const showDetails = scope.period || scope.multipleGroups
	const hasRemaining = cleanings.some(item => item.recorded)
	const cards: MetricCardData[] = [
		{
			id: 'total-remaining',
			parts: [value(formatInt(totalRemaining)), unit('кг')],
			label: 'Остаток всего',
			empty: !hasRemaining,
		},
	]

	if (showDetails && minRemaining) {
		cards.push({
			id: 'min-remaining',
			parts: [value(formatInt(minRemaining.percent)), unit('%')],
			label: 'Минимальный остаток',
			badge: scope.multipleGroups
				? groupBadge(minRemaining.sample.groupTitle)
				: undefined,
			aside: scope.period
				? `Было ${moment(minRemaining.sample.date).format('DD.MM.YY')}`
				: undefined,
		})
	}

	if (showDetails && maxRemaining) {
		cards.push({
			id: 'max-remaining',
			parts: [value(formatInt(maxRemaining.percent)), unit('%')],
			label: 'Максимальный остаток',
			badge: scope.multipleGroups
				? groupBadge(maxRemaining.sample.groupTitle)
				: undefined,
			aside: scope.period
				? `Было ${moment(maxRemaining.sample.date).format('DD.MM.YY')}`
				: undefined,
		})
	}

	cards.push({
		id: 'avg-remaining',
		parts: [value(formatInt(avgRemaining ?? 0)), unit('%')],
		label: 'Средний остаток',
		empty: avgRemaining == null,
	})

	cards.push({
		id: 'cleanup-time',
		parts: [value(formatInt(compliance ?? 0)), unit('%')],
		label: 'Соблюдение времени уборки',
		empty: compliance == null,
		badge:
			late == null ? undefined : alertBadge(`${formatInt(late)}% опозданий`),
	})

	if (showDetails) {
		cards.push({
			id: 'cleanup-deviation',
			parts: [value(formatInt(maxDeviation?.minutes ?? 0)), unit('мин')],
			label: 'Максимальное отклонение от времени уборки',
			empty: !maxDeviation,
			badge:
				scope.multipleGroups && maxDeviation
					? groupBadge(maxDeviation.groupTitle)
					: undefined,
			aside: maxDeviation
				? `было ${maxDeviation.fact.format('HH:mm:ss')}`
				: undefined,
		})
		cards.push({
			id: 'missed-cleanups',
			parts: [value(formatInt(missedCount)), unit('уборок')],
			label: 'Пропущено уборок',
			empty: plannedCount === 0,
			badge:
				missedPercent == null
					? undefined
					: {
							text: `${formatInt(missedPercent)}%`,
							tone: 'percent',
						},
		})
	}

	return cards
}
